import { z } from 'zod';
import {
  auditFieldsShape,
  localDateSchema,
  softDeleteShape,
  stickerRefSchema,
  utcTimestampSchema,
  uuidSchema,
} from './primitives';

export const TASK_KINDS = ['task', 'note'] as const;
export const taskKindSchema = z.enum(TASK_KINDS);

/**
 * resolved 與 closed 都代表「做完了」，cancelled 代表「不做了」。
 * 兩者必須能分開查詢與統計（spec 9.1）。
 */
export const TASK_STATUSES = [
  'new',
  'in_progress',
  'waiting',
  'resolved',
  'closed',
  'cancelled',
] as const;
export const taskStatusSchema = z.enum(TASK_STATUSES);

/** UI 的 status 下拉只能提供這三個，其餘只能經由明確的結束操作進入（data-model 3.2）。 */
export const SELECTABLE_TASK_STATUSES = ['new', 'in_progress', 'waiting'] as const;

export const TASK_PRIORITIES = ['normal', 'important', 'urgent'] as const;
export const taskPrioritySchema = z.enum(TASK_PRIORITIES);

export const taskSchema = z.object({
  id: uuidSchema,
  /** 本機流水編號，顯示為 #123。僅供顯示，永不作為關聯鍵。 */
  number: z.number().int().positive(),

  kind: taskKindSchema,
  subject: z.string().min(1, '標題是必填'),
  description: z.string().nullable(),
  status: taskStatusSchema,
  priority: taskPrioritySchema,

  taskTypeId: uuidSchema.nullable(),
  sourceTypeId: uuidSchema.nullable(),
  topic: z.string().nullable(),
  tagIds: z.array(uuidSchema),
  stickers: z.array(stickerRefSchema),

  startDate: localDateSchema.nullable(),
  dueDate: localDateSchema.nullable(),

  waitingSince: utcTimestampSchema.nullable(),
  linkedTaskId: uuidSchema.nullable(),
  recurrenceRuleId: uuidSchema.nullable(),

  progress: z.number().int().min(0).max(100).nullable(),
  estimatedMinutes: z.number().int().nonnegative().nullable(),
  actualMinutes: z.number().int().nonnegative().nullable(),

  completedAt: utcTimestampSchema.nullable(),
  cancelledAt: utcTimestampSchema.nullable(),
  cancelReason: z.string().nullable(),

  archivedAt: utcTimestampSchema.nullable(),
  removedFromBoardAt: utcTimestampSchema.nullable(),

  ...softDeleteShape,
  ...auditFieldsShape,
});

export const TASK_CONTACT_ROLES = [
  'reminder_target',
  'awaiting_reply',
  'meeting_participant',
  'other',
] as const;
export const taskContactRoleSchema = z.enum(TASK_CONTACT_ROLES);

export const taskContactSchema = z.object({
  id: uuidSchema,
  taskId: uuidSchema,
  /** 聯絡人被永久刪除後設為 null，此時完全依賴快照顯示。 */
  contactId: uuidSchema.nullable(),
  roleInTask: taskContactRoleSchema.nullable(),
  nameSnapshot: z.string(),
  jobTitleSnapshot: z.string().nullable(),
  organizationSnapshot: z.string().nullable(),
  ...auditFieldsShape,
});

export const RECURRENCE_FREQUENCIES = ['daily', 'weekly', 'monthly', 'yearly'] as const;
export const recurrenceFrequencySchema = z.enum(RECURRENCE_FREQUENCIES);

export const recurrenceRuleSchema = z.object({
  id: uuidSchema,
  frequency: recurrenceFrequencySchema,
  /** weekly 專用：0 至 6，0 為星期日，至少一個。 */
  weekdays: z.array(z.number().int().min(0).max(6)).min(1).nullable(),
  monthDay: z.number().int().min(1).max(31).nullable(),
  yearMonth: z.number().int().min(1).max(12).nullable(),
  yearDay: z.number().int().min(1).max(31).nullable(),
  endDate: localDateSchema.nullable(),
  ...auditFieldsShape,
});

export type TaskKind = z.infer<typeof taskKindSchema>;
export type TaskStatus = z.infer<typeof taskStatusSchema>;
export type TaskPriority = z.infer<typeof taskPrioritySchema>;
export type Task = z.infer<typeof taskSchema>;
export type TaskContactRole = z.infer<typeof taskContactRoleSchema>;
export type TaskContact = z.infer<typeof taskContactSchema>;
export type RecurrenceFrequency = z.infer<typeof recurrenceFrequencySchema>;
export type RecurrenceRule = z.infer<typeof recurrenceRuleSchema>;
